import { useMemo } from "react";
import { useAuth } from "react-oidc-context";
import { config } from "../../config";

/**
 * Cliente del asistente de IA del ERP (endpoints `/api/v1/assistant` del backend).
 * Escrito a mano —tipos incluidos— a propósito: el contrato es nuevo y aún no vive
 * en el cliente generado `@farmacenter/api`; cuando aterrice, migra y este módulo
 * desaparece. Reutiliza el token vigente de la sesión, igual que el resto.
 */

export interface HerramientaAsistente {
  name: string;
  label: string;
}

export interface EstadoAsistente {
  /** El asistente está operativo (hay clave configurada en el backend). */
  enabled: boolean;
  model: string | null;
  tools: HerramientaAsistente[];
}

export interface TurnoChat {
  role: "user" | "assistant";
  text: string;
}

export interface ContextoAsistente {
  tenantId: string;
  legalEntityId: string | null;
  branchId: string | null;
}

/** Una acción de escritura que Nova propone y espera que confirmes en el canvas. */
export interface AccionPendiente {
  tool: string;
  label: string;
  resumen: string;
  params: Record<string, unknown>;
}

export interface RespuestaChat {
  reply: string;
  /** Nombres de las herramientas que consultó para responder (transparencia). */
  toolsUsed: string[];
  /** Si Nova propone una escritura, la acción a confirmar en el canvas; null si no. */
  pendingAction?: AccionPendiente | null;
}

/** Resultado de ejecutar una acción confirmada. */
export interface ResultadoAccion {
  ok: boolean;
  mensaje: string;
  /** Si el ERP rechazó por validación: errores por campo (clave del campo → motivo). */
  camposConError?: Record<string, string>;
}

/** Un usuario conectado al ERP (para "Conectados al ERP" del Panel). */
export interface Conectado {
  id: string;
  nombre: string;
}

/** Respuesta del latido de presencia: quién está conectado ahora mismo. */
export interface PresenciaRespuesta {
  conectados: Conectado[];
}

/** Permisos efectivos de la sesión según el servidor (scopes con punto). */
export interface PermisosRespuesta {
  scopes: string[];
}

/** Una línea de producto leída de una factura por la IA (texto tal cual; "" si no aparece). */
export interface LineaExtraida {
  descripcion: string;
  codigo: string;
  cantidad: string;
  precioUnitario: string;
  iva: string;
  lote: string;
  vencimiento: string;
}

/** Datos de una factura de proveedor extraídos por la IA, para precargar (sugerencia). */
export interface FacturaExtraida {
  proveedorNombre: string;
  proveedorNit: string;
  numeroFactura: string;
  fechaEmision: string;
  fechaVencimiento: string;
  subtotal: string;
  iva: string;
  total: string;
  lineas: LineaExtraida[];
  notas: string;
}

/** Configuración del asistente, editable desde Organización. Nunca expone la clave cruda. */
export interface AsistenteConfig {
  /** El asistente está operativo (hay clave, de la base o del entorno). */
  enabled: boolean;
  model: string;
  baseUrl: string | null;
  /** Hay una clave configurada. */
  hasKey: boolean;
  /** Pista enmascarada de la clave activa (p. ej. "cfut…8ffa"), nunca la clave completa. */
  keyHint: string | null;
  /** De dónde sale la clave activa. */
  source: "db" | "env" | "none";
}

/** Cambios a la configuración. La clave sólo se toca si se envía; vacía = borrar. */
export interface GuardarConfig {
  apiKey?: string;
  model?: string;
  baseUrl?: string;
  /** Borra la clave guardada (vuelve a la del entorno, si hay). */
  clearKey?: boolean;
}

/**
 * Estado del proveedor de IA según las últimas llamadas reales: `up` (respondió), `down`
 * (falló, con el motivo en `detail`) o `unknown` (aún no se ha llamado desde el arranque).
 */
export interface SaludAsistente {
  status: "up" | "down" | "unknown";
  /** Instante del último contacto con el proveedor (ISO-8601), o null. */
  checkedAt: string | null;
  /** Instante de la última respuesta correcta (ISO-8601), o null. */
  lastSuccessAt: string | null;
  /** Motivo legible del último fallo, o null. */
  detail: string | null;
  /** Fallos seguidos desde el último éxito (0 si el último fue bien). */
  consecutiveFailures: number;
}

export interface AsistenteApi {
  estado: () => Promise<EstadoAsistente>;
  chat: (messages: TurnoChat[], context: ContextoAsistente) => Promise<RespuestaChat>;
  /**
   * Ejecuta una acción de escritura que el usuario confirmó (y quizá editó) en el canvas.
   * `idempotencyKey` (opcional) se reutiliza entre reintentos de una misma confirmación: el
   * backend la propaga al ERP para que un reintento tras un timeout no cree un duplicado.
   */
  ejecutarAccion: (
    tool: string,
    params: Record<string, unknown>,
    context: ContextoAsistente,
    idempotencyKey?: string,
  ) => Promise<ResultadoAccion>;
  /** Latido de presencia: registra que sigues activo y devuelve quién está conectado al ERP. */
  presencia: () => Promise<PresenciaRespuesta>;
  /** Permisos efectivos de la sesión según el servidor (para "Qué puedes hacer" del Panel). */
  permisos: () => Promise<PermisosRespuesta>;
  /** Escanea una factura de proveedor (imagen o PDF en base64) y devuelve sus datos. */
  escanearFactura: (mediaType: string, imagenBase64: string) => Promise<FacturaExtraida>;
  /** Configuración del asistente (Organización): leer y guardar. */
  config: () => Promise<AsistenteConfig>;
  guardarConfig: (cambios: GuardarConfig) => Promise<AsistenteConfig>;
  /** Estado del proveedor según las últimas llamadas (para el indicador de Organización). */
  salud: () => Promise<SaludAsistente>;
  /** Prueba el proveedor con una llamada mínima y devuelve su estado (botón "probar conexión"). */
  probar: () => Promise<SaludAsistente>;
}

async function leerError(res: Response): Promise<Error> {
  try {
    const cuerpo = (await res.json()) as { detail?: string; title?: string };
    return new Error(cuerpo.detail ?? cuerpo.title ?? `Error ${res.status}`);
  } catch {
    return new Error(`Error ${res.status}`);
  }
}

export function useAsistenteApi(): AsistenteApi {
  const auth = useAuth();

  return useMemo<AsistenteApi>(() => {
    async function pedir<T>(
      metodo: string,
      ruta: string,
      cuerpo?: unknown,
      idempotencyKey?: string,
    ): Promise<T> {
      const token = auth.user?.access_token ?? null;
      // El backend exige Idempotency-Key (UUID) en toda petición que modifica datos; un GET
      // no la lleva. Por defecto una clave única por llamada; pero si el llamador pasa una
      // (ejecutar una acción del canvas), se REUSA entre reintentos para que el ERP deduplique.
      const modifica = metodo.toUpperCase() !== "GET";
      const res = await fetch(`${config.apiBaseUrl}${ruta}`, {
        method: metodo,
        headers: {
          ...(cuerpo !== undefined ? { "Content-Type": "application/json" } : {}),
          ...(modifica ? { "Idempotency-Key": idempotencyKey ?? crypto.randomUUID() } : {}),
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        ...(cuerpo !== undefined ? { body: JSON.stringify(cuerpo) } : {}),
      });
      if (!res.ok) {
        throw await leerError(res);
      }
      return (await res.json()) as T;
    }

    return {
      estado: () => pedir<EstadoAsistente>("GET", "/api/v1/assistant/status"),
      chat: (messages, context) =>
        pedir<RespuestaChat>("POST", "/api/v1/assistant/chat", { messages, context }),
      ejecutarAccion: (tool, params, context, idempotencyKey) =>
        pedir<ResultadoAccion>(
          "POST",
          "/api/v1/assistant/actions/execute",
          { tool, params, context },
          idempotencyKey,
        ),
      presencia: () => pedir<PresenciaRespuesta>("POST", "/api/v1/assistant/presence/heartbeat"),
      permisos: () => pedir<PermisosRespuesta>("GET", "/api/v1/assistant/whoami"),
      escanearFactura: (mediaType, imagenBase64) =>
        pedir<FacturaExtraida>("POST", "/api/v1/assistant/ocr/factura-compra", {
          mediaType,
          imagenBase64,
        }),
      config: () => pedir<AsistenteConfig>("GET", "/api/v1/assistant/settings"),
      guardarConfig: (cambios) =>
        pedir<AsistenteConfig>("PUT", "/api/v1/assistant/settings", cambios),
      salud: () => pedir<SaludAsistente>("GET", "/api/v1/assistant/health"),
      probar: () => pedir<SaludAsistente>("POST", "/api/v1/assistant/health/probe"),
    };
  }, [auth]);
}
